import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, Plus, Search, X } from "lucide-react";

/**
 * V197 — Small searchable picker (single or multi) with grouped options and free entry.
 * groups: [{ label, items: string[] | {value,label,hint}[] }]
 */
const norm = (it) => (typeof it === "string" ? { value: it, label: it } : it);

const ManualTag = ({ testid }) => (
    <span data-testid={testid} className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">manual</span>
);

export default function PickList({ groups = [], value, onChange, multi = false, placeholder = "Select…", testid, allowCustom = true, manualLabel = "", onSaveMaster }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [manual, setManual] = useState(null); // { text, save } while the manual-entry box is open
    const box = useRef(null);
    const selected = multi ? value || [] : value ? [value] : [];
    useEffect(() => {
        if (!open) return undefined;
        document.body.classList.add("fee-picklist-open"); // hides the admin mobile FAB while a menu is open
        return () => document.body.classList.remove("fee-picklist-open");
    }, [open]);
    useEffect(() => {
        const close = (e) => { if (box.current && !box.current.contains(e.target)) { setOpen(false); setQ(""); setManual(null); } };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);
    const shown = useMemo(() => {
        const t = q.trim().toLowerCase();
        const seen = new Set();
        return groups.map((g) => ({
            label: g.label,
            items: g.items.map(norm).filter((it) => !seen.has(it.value) && seen.add(it.value)
                && (!t || `${it.label} ${it.hint || ""}`.toLowerCase().includes(t))),
        })).filter((g) => g.items.length);
    }, [groups, q]);
    const exact = shown.some((g) => g.items.some((it) => it.value.toLowerCase() === q.trim().toLowerCase()));
    const labelOf = (v) => groups.flatMap((g) => g.items.map(norm)).find((it) => it.value === v)?.label || v;
    const known = useMemo(() => new Set(groups.flatMap((g) => g.items.map((it) => norm(it).value.toLowerCase()))), [groups]);
    const isManual = (v) => !known.has(String(v).toLowerCase());
    const pick = (v) => {
        if (multi) onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
        else { onChange(v === value ? "" : v); setOpen(false); }
        setQ("");
    };
    const addManual = async () => {
        const v = (manual?.text || "").replace(/\s+/g, " ").trim();
        if (!v || /^(null|undefined|none|nan)$/i.test(v)) return;
        const existing = groups.flatMap((g) => g.items.map(norm)).find((it) => it.value.toLowerCase() === v.toLowerCase());
        const finalV = existing?.value || v;
        if (!multi || !selected.includes(finalV)) pick(finalV);
        if (manual.save && existing) toast.info(`“${finalV}” is already in the list`);
        else if (manual.save && onSaveMaster) await onSaveMaster(finalV);
        setManual(null);
        if (!multi) setOpen(false);
    };
    return (
        <div className="relative" ref={box} data-testid={testid}>
            <button type="button" onClick={() => { setOpen((o) => !o); setQ(""); setManual(null); }} data-testid={`${testid}-trigger`}
                className="w-full min-h-[44px] px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-left text-sm flex items-center gap-1.5 flex-wrap focus:border-[#1E3A8A] outline-none">
                {selected.length === 0 && <span className="text-slate-400 flex-1">{placeholder}</span>}
                {!multi && selected[0] && (
                    <span className="flex-1 min-w-0 flex items-center gap-1.5">
                        <span className="font-semibold text-[#0F172A] truncate">{labelOf(selected[0])}</span>
                        {manualLabel && isManual(selected[0]) && <ManualTag testid={`${testid}-manual-tag`} />}
                    </span>
                )}
                {multi && selected.map((v) => (
                    <span key={v} className="inline-flex items-center gap-1 bg-[#EEF2FF] text-[#1E3A8A] rounded-md px-2 py-0.5 text-xs font-semibold">
                        {v}
                        {manualLabel && isManual(v) && <ManualTag testid={`${testid}-manual-tag`} />}
                        <span role="button" tabIndex={0} aria-label={`Remove ${v}`} onClick={(e) => { e.stopPropagation(); pick(v); }}
                            className="hover:text-rose-600"><X className="w-3 h-3" /></span>
                    </span>
                ))}
                <ChevronDown className="w-4 h-4 text-slate-400 ml-auto shrink-0" />
            </button>
            {open && (
                <div className="absolute z-[80] mt-1 w-full min-w-[220px] bg-white border border-slate-200 rounded-xl shadow-lg p-2" data-testid={`${testid}-menu`}>
                    <div className="relative mb-1">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search or type…" data-testid={`${testid}-search`}
                            onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { e.preventDefault(); pick(shown[0]?.items[0]?.value || q.trim()); } }}
                            className="w-full h-9 pl-8 pr-2 rounded-md border border-slate-200 text-sm outline-none focus:border-[#1E3A8A]" />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                        {manualLabel && !manual && (
                            <button type="button" onClick={() => setManual({ text: q.trim(), save: false })} data-testid={`${testid}-add-manual`}
                                className="w-full text-left px-2 py-2 text-sm rounded-md hover:bg-slate-50 text-[#1E3A8A] font-semibold inline-flex items-center gap-1.5">
                                <Plus className="w-3.5 h-3.5" /> Add {manualLabel} manually
                            </button>
                        )}
                        {manual && (
                            <div className="p-2 mb-1 rounded-lg bg-[#F8FAFC] border border-slate-200 space-y-2" data-testid={`${testid}-manual-box`}>
                                <input autoFocus value={manual.text} maxLength={80} placeholder={`Type ${manualLabel.toLowerCase()} name`} data-testid={`${testid}-manual-input`}
                                    onChange={(e) => setManual((m) => ({ ...m, text: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } if (e.key === "Escape") setManual(null); }}
                                    className="w-full h-10 px-2 rounded-md border border-slate-300 text-sm outline-none focus:border-[#1E3A8A] bg-white" />
                                <label className="flex items-center gap-2 text-xs text-slate-600 min-h-[36px] cursor-pointer">
                                    <input type="checkbox" className="w-4 h-4 accent-[#1E3A8A]" checked={manual.save} onChange={(e) => setManual((m) => ({ ...m, save: e.target.checked }))} data-testid={`${testid}-manual-save`} />
                                    Save to master list <span className="text-slate-400">(Fee Reminder dropdowns only)</span>
                                </label>
                                <div className="flex gap-2 justify-end">
                                    <button type="button" onClick={() => setManual(null)} data-testid={`${testid}-manual-cancel`} className="h-10 px-4 rounded-md border border-slate-300 text-xs font-semibold">Cancel</button>
                                    <button type="button" onClick={addManual} disabled={!manual.text.trim()} data-testid={`${testid}-manual-add`}
                                        className="h-10 px-4 rounded-md bg-[#1E3A8A] text-white text-xs font-bold disabled:opacity-50">Add</button>
                                </div>
                            </div>
                        )}
                        {allowCustom && q.trim() && !exact && (
                            <button type="button" onClick={() => pick(q.trim())} data-testid={`${testid}-custom`}
                                className="w-full text-left px-2 py-2 text-sm rounded-md hover:bg-slate-50 text-[#1E3A8A] font-semibold inline-flex items-center gap-1.5">
                                <Plus className="w-3.5 h-3.5" /> Use “{q.trim()}”
                            </button>
                        )}
                        {shown.map((g) => (
                            <div key={g.label}>
                                <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">{g.label}</div>
                                {g.items.map((it) => (
                                    <button type="button" key={it.value} onClick={() => pick(it.value)} data-testid={`${testid}-opt`}
                                        className="w-full text-left px-2 py-2 text-sm rounded-md hover:bg-slate-50 flex items-center gap-2">
                                        <span className={`w-4 h-4 shrink-0 grid place-items-center rounded border ${selected.includes(it.value) ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "border-slate-300"}`}>
                                            {selected.includes(it.value) && <Check className="w-3 h-3" />}
                                        </span>
                                        <span className="min-w-0"><span className="block truncate">{it.label}</span>
                                            {it.hint && <span className="block text-[11px] text-slate-400 truncate">{it.hint}</span>}</span>
                                    </button>
                                ))}
                            </div>
                        ))}
                        {!shown.length && !q.trim() && <div className="px-2 py-3 text-sm text-slate-400">No options</div>}
                    </div>
                </div>
            )}
        </div>
    );
}
